import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, CheckCircle2, FileCode2, FolderOpen, Upload, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NmsConfigurationList } from '@/components/nms/NmsConfigurationList'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { matchNmsFileToProjects, NmsFileMatch } from '@/lib/nmsConfigurations'
import { Project, projectsApi } from '@/lib/projectsApi'
import { canUpload } from '@/lib/roles'

function projectLabel(project: Project): string {
  return [
    project.shipyard_name,
    project.hull_number,
    project.vessel_name || project.project_name,
  ].filter(Boolean).join(' / ')
}

export const NmsConfigurationsPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()
  const { success, error: showError } = useToast()
  const folderInputRef = useRef<HTMLInputElement | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [matches, setMatches] = useState<NmsFileMatch[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    projectsApi.listProjects()
      .then(setProjects)
      .catch((err: any) => showError(err.message ?? 'Failed to load vessels'))
      .finally(() => setLoadingProjects(false))
  }, [showError])

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => projectLabel(a).localeCompare(projectLabel(b))),
    [projects],
  )
  const readyCount = matches.filter(match => match.projectId).length
  const unresolvedCount = matches.length - readyCount

  const selectFolder = (files: FileList | null) => {
    if (!files) return
    const iniFiles = [...files].filter(file => /\.ini$/i.test(file.name))
    if (!iniFiles.length) {
      showError('The selected folder does not contain any .ini files')
      return
    }
    setMatches(iniFiles.map(file => matchNmsFileToProjects(file, projects)))
  }

  const assignProject = (index: number, projectId: string) => {
    setMatches(current => current.map((match, itemIndex) =>
      itemIndex === index
        ? { ...match, projectId, status: projectId ? 'matched' : match.matchingProjects.length > 1 ? 'ambiguous' : 'unmatched' }
        : match,
    ))
  }

  const uploadReviewed = async () => {
    const ready = matches.filter(match => match.projectId)
    if (!ready.length) return
    setUploadProgress({ done: 0, total: ready.length })
    let saved = 0
    let duplicates = 0
    const failed = new Set<File>()
    for (let i = 0; i < ready.length; i += 1) {
      const item = ready[i]
      try {
        const result = await projectsApi.uploadNmsConfiguration(
          item.projectId,
          item.file,
          item.relativePath,
        )
        if (result.duplicate) duplicates += 1
        else saved += 1
      } catch (err: any) {
        failed.add(item.file)
        showError(`${item.file.name}: ${err.message ?? 'upload failed'}`)
      }
      setUploadProgress({ done: i + 1, total: ready.length })
    }
    setUploadProgress(null)
    setMatches(current => current.filter(item => !item.projectId || failed.has(item.file)))
    if (saved || duplicates) {
      success(`Saved ${saved} configuration${saved === 1 ? '' : 's'}${duplicates ? `; ${duplicates} unchanged duplicate${duplicates === 1 ? '' : 's'} skipped` : ''}`)
      setRefreshToken(token => token + 1)
    }
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  return (
    <div className="min-h-screen bg-[#f4f6f8] text-slate-900 dark:bg-slate-900 dark:text-white">
      <div className="mx-auto max-w-[1680px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em]">
              <FileCode2 className="h-5 w-5 text-blue-800" />
              {t('nms.title')}
            </h1>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {t('nms.subtitle')}
            </p>
          </div>
          {canUpload(user) && (
            <>
              <input
                ref={element => {
                  folderInputRef.current = element
                  element?.setAttribute('webkitdirectory', '')
                  element?.setAttribute('directory', '')
                }}
                type="file"
                accept=".ini"
                multiple
                className="hidden"
                onChange={event => selectFolder(event.target.files)}
              />
              <Button
                onClick={() => folderInputRef.current?.click()}
                disabled={loadingProjects || Boolean(uploadProgress)}
              >
                <FolderOpen className="h-4 w-4" />
                {t('nms.importFolder')}
              </Button>
            </>
          )}
        </div>

        {matches.length > 0 && (
          <section className="mb-5 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold">{t('nms.reviewImport')}</h2>
                <p className="text-xs text-slate-400">
                  {readyCount} ready · {unresolvedCount} require vessel selection. Nothing is saved until you confirm.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setMatches([])} disabled={Boolean(uploadProgress)}>
                Cancel
              </Button>
              <Button onClick={() => void uploadReviewed()} disabled={!readyCount || Boolean(uploadProgress)}>
                <Upload className="h-4 w-4" />
                {uploadProgress
                  ? `Uploading ${uploadProgress.done}/${uploadProgress.total}`
                  : `Upload ${readyCount} matched file${readyCount === 1 ? '' : 's'}`}
              </Button>
            </div>
            <div className="max-h-[430px] overflow-auto">
              <table className="w-full min-w-[900px] border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 font-medium">File</th>
                    <th className="px-3 py-2.5 font-medium">Detected hull</th>
                    <th className="px-3 py-2.5 font-medium">Vessel assignment</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((match, index) => (
                    <tr key={`${match.relativePath}-${index}`} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-3 py-2.5">
                        {match.projectId ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300">
                            <CheckCircle2 className="h-4 w-4" /> Ready
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                            <XCircle className="h-4 w-4" />
                            {match.status === 'ambiguous' ? 'Ambiguous' : 'Unmatched'}
                          </span>
                        )}
                      </td>
                      <td className="max-w-[400px] px-3 py-2.5">
                        <div className="truncate font-medium" title={match.relativePath}>{match.file.name}</div>
                        <div className="truncate text-xs text-slate-400" title={match.relativePath}>{match.relativePath}</div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">
                        {match.hullCandidates.join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <select
                          value={match.projectId}
                          onChange={event => assignProject(index, event.target.value)}
                          className="h-9 w-full min-w-[280px] rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-900"
                        >
                          <option value="">Select vessel…</option>
                          {sortedProjects.map(project => (
                            <option key={project.id} value={project.id}>{projectLabel(project)}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <NmsConfigurationList
          showProject
          refreshToken={refreshToken}
          onChanged={() => setRefreshToken(token => token + 1)}
        />
      </div>
    </div>
  )
}
